import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import {
    IBalooGroup,
    IBalooMatch,
    IBalooMatchData,
    IBalooMatchTab,
    IBalooUser,
    IBalooUserMatch,
} from '@fi-app/external-platform-integration/baloo/baloo';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { BalooService } from '@fi-app/external-platform-integration/baloo/baloo.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { BehaviorSubject } from 'rxjs';
import { ObjectTypes } from '@fi-shared/types';
import { cloneDeep } from '@fi-shared/utils';
import { BalooSyncTabIndex } from '@fi-app/external-platform-integration/baloo/baloo-sync-log-summary-items.enum';

@Component({
    selector: 'baloo-match',
    templateUrl: './baloo-match.component.html',
    styleUrls: ['./baloo-match.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'baloo-match' },
    standalone: false,
})
export class BalooMatchComponent implements OnInit {
    @Input() balooMatchData: IBalooMatchData;

    isContentShown$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    balooGroups: IBalooGroup[] = [];
    balooUsers: IBalooUser[] = [];
    selectedTabIndex$ = new BehaviorSubject<number>(0);
    matchTabs: IBalooMatchTab[] = [];

    private initialBalooMatchData: IBalooMatchData;

    constructor(
        private balooService: BalooService,
        private customerDataService: FiCustomerDataService,
        private saveChangesWarningService: SaveChangesWarningService,
        private cdr: ChangeDetectorRef,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
    ) {}

    ngOnInit(): void {
        this.checkShowConfirmationDialog().then((data) => {
            if (data.result) {
                this.setBalooData();
            } else {
                window.history.back();
            }
        });
    }

    private checkShowConfirmationDialog() {
        return new Promise<{ result: boolean }>((resolve, reject) => {
            if (!this.balooMatchData.ShowMatchConfirmMessage) {
                resolve({ result: true });
            } else {
                this.fiDialogsService
                    .confirm({
                        isTopCloseButtonShown: true,
                        headerIconClass: 'far fa-bookmark',
                        headerText: this.appTextService.get('baloo.match_warning_header'),
                        messageText: this.appTextService.get('baloo.match_warning_message', {
                            '{0}': this.customerDataService.customerData.DisplayName,
                        }),
                        noButtonText: this.appTextService.get('common.close'),
                        yesButtonText: this.appTextService.get('common.continue'),
                        config: {
                            panelClass: 'fi-rounded-dialog-panel',
                            maxWidth: '585px',
                            autoFocus: false,
                        },
                    })
                    .onYesButtonClick(() => {
                        this.balooService.setMatchConfirmMessageShown();
                        resolve({ result: true });
                    })
                    .onNoButtonClick(() => {
                        resolve({ result: false });
                    });
            }
        });
    }

    private setBalooData() {
        this.setMatchTabsData();
        this.isContentShown$.next(true);
        this.setBalooMatches(this.balooMatchData);
    }

    private setBalooMatches(balooMatchData: IBalooMatchData) {
        this.saveChangesWarningService.stop();
        this.initialBalooMatchData = cloneDeep(balooMatchData);
        this.balooMatchData = cloneDeep(balooMatchData);
        this.balooGroups = this.balooMatchData.InitialBalooGroups;
        this.balooUsers = this.balooMatchData.InitialBalooUsers;
        this.saveChangesWarningService.start(() => this.balooMatchData, {
            headerText: this.appTextService.get('common.leave_page_without_saving_warning_header'),
            messageText: this.appTextService.get('common.leave_page_without_saving_warning_message'),
        });
        this.cdr.markForCheck();
    }

    private setMatchTabsData() {
        this.matchTabs = [
            {
                tabLabel: this.appTextService.get('common.units'),
                headerLabel: this.appTextService.get('baloo.match_units_tab_header', {
                    '{0}': this.customerDataService.customerData.DisplayName,
                }),
                descriptionLabel: this.appTextService.get('baloo.match_units_tab_description', {
                    '{0}': this.customerDataService.customerData.DisplayName,
                }),
                isButtonVisible: true,
                buttonLabel: this.appTextService.get('baloo.match_import_groups'),
                onButtonClick: () => this.onImportGroupsClick(ObjectTypes.BusinessUnit),
            },
            {
                tabLabel: this.appTextService.get('common.roles'),
                headerLabel: this.appTextService.get('baloo.match_roles_tab_header', {
                    '{0}': this.customerDataService.customerData.DisplayName,
                }),
                descriptionLabel: this.appTextService.get('baloo.match_roles_tab_description', {
                    '{0}': this.customerDataService.customerData.DisplayName,
                }),
                isButtonVisible: true,
                buttonLabel: this.appTextService.get('baloo.match_import_groups'),
                onButtonClick: () => this.onImportGroupsClick(ObjectTypes.Role),
            },
            {
                tabLabel: this.appTextService.get('common.users'),
                headerLabel: this.appTextService.get('baloo.match_users_tab_header', {
                    '{0}': this.customerDataService.customerData.DisplayName,
                }),
                descriptionLabel: this.appTextService.get('baloo.match_users_tab_description', {
                    '{0}': this.customerDataService.customerData.DisplayName,
                }),
                isButtonVisible: false,
            },
        ];
    }

    getFilterItemsByTabIndex(): string[] {
        const balooGroupNames: string[] = this.balooGroups.map((balooGroup: IBalooGroup) => balooGroup.GroupName);
        switch (this.selectedTabIndex$.value) {
            case BalooSyncTabIndex.Unit:
                return [
                    ...this.balooMatchData.UnitMatches.map((unitMatch: IBalooMatch) => unitMatch.ObjectName),
                    ...balooGroupNames,
                ];
            case BalooSyncTabIndex.Role:
                return [
                    ...this.balooMatchData.RoleMatches.map((unitMatch: IBalooMatch) => unitMatch.ObjectName),
                    ...balooGroupNames,
                ];
            case BalooSyncTabIndex.User:
                return [
                    ...this.balooUsers.map((balooUser) => balooUser.UserName),
                    ...this.balooMatchData.UserMatches.map(
                        (userMatch: IBalooUserMatch) => userMatch.UserProfile.FullName,
                    ),
                ];
        }
    }

    filterItemsName: string[] = [];
    onSelectedFilterItemsChange(filterNames: string[]) {
        this.filterItemsName = filterNames;
    }

    getFilteredBalooItems(matches: IBalooMatch[]): IBalooMatch[] {
        if (!this.filterItemsName?.length) return matches;

        return matches.filter((match: IBalooMatch) => {
            return this.filterItemsName.some((filterItemName: string) => {
                const relatedBalooGroup: IBalooGroup = this.getBalooGroupById(match.BalooId);
                return (
                    match.ObjectName.toLowerCase().includes(filterItemName.toLowerCase()) ||
                    relatedBalooGroup?.GroupName.toLowerCase().includes(filterItemName.toLowerCase())
                );
            });
        });
    }
    private getBalooGroupById(balooId: string): IBalooGroup {
        return this.balooGroups.find((balooGroup: IBalooGroup) => balooGroup.GroupId === balooId);
    }

    getFilteredBalooUsers(): IBalooUserMatch[] {
        if (!this.filterItemsName?.length) return this.balooMatchData.UserMatches;

        return this.balooMatchData.UserMatches.filter((userMatch: IBalooUserMatch) => {
            return this.filterItemsName.some((filterItemName: string) => {
                const relatedBalooUser: IBalooUser = this.getBalooUserById(userMatch.BalooId);
                return (
                    userMatch.UserProfile.FullName.includes(filterItemName) ||
                    relatedBalooUser?.UserName.includes(filterItemName)
                );
            });
        });
    }
    private getBalooUserById(balooUserId: string): IBalooUser {
        return this.balooUsers.find((balooUser: IBalooUser) => balooUser.UserId === balooUserId);
    }

    saveMatches() {
        this.balooService.saveMatches(this.balooMatchData).then(() => this.getMatchData());
    }

    private getMatchData() {
        this.balooService
            .getMatchData()
            .then((balooMatchData: IBalooMatchData) => this.setBalooMatches(balooMatchData));
    }

    onImportGroupsClick(objectType: ObjectTypes) {
        this.balooService.getBalooGroups().then((balooGroups: IBalooGroup[]) => {
            if (!balooGroups?.length) {
                this.showEmptyImportDialog(objectType);
                return;
            }
            this.balooGroups = balooGroups;
            this.cdr.markForCheck();
        });
    }

    private showEmptyImportDialog(objectType: ObjectTypes) {
        this.fiDialogsService.notify({
            isTopCloseButtonShown: true,
            colorClass: 'orange',
            headerIconClass: 'far fa-exclamation-circle orange',
            headerText: this.appTextService.get('baloo.no_groups_found_header'),
            messageText: this.getEmptyImportDialogMessageText(objectType),
            yesButtonText: this.appTextService.get('ok'),
            config: {
                panelClass: 'fi-rounded-dialog-panel',
                maxWidth: '640px',
            },
        });
    }

    private getEmptyImportDialogMessageText(objectType: ObjectTypes): string {
        switch (objectType) {
            case ObjectTypes.BusinessUnit:
                return this.appTextService.get('baloo.no_groups_found_message_unit', {
                    '{0}': this.customerDataService.customerData.DisplayName,
                });
            case ObjectTypes.Role:
                return this.appTextService.get('baloo.no_groups_found_message_role', {
                    '{0}': this.customerDataService.customerData.DisplayName,
                });
            case ObjectTypes.User:
                return this.appTextService.get('user', {
                    '{0}': this.customerDataService.customerData.DisplayName,
                });
        }
    }

    importUsers() {
        this.balooService
            .getBalooUsers()
            .then((balooUsers: IBalooUser[]) => {
                this.balooUsers = balooUsers;
                this.setCurrentTabIndex(BalooSyncTabIndex.User);
                this.cdr.markForCheck();
            })
            .catch(() => this.setCurrentTabIndex(this.selectedTabIndex$.value));
    }

    selectedTabIndexChange(newTabIndex: number) {
        if (newTabIndex === this.selectedTabIndex$.value) return;

        if (this.isBalooMatchDataChanged()) {
            this.setCurrentTabIndex(this.selectedTabIndex$.value);
            this.saveChangesWarningService.showConfirmDialog().onYesButtonClick(() => {
                this.setBalooMatches(this.initialBalooMatchData);
                this.onSelectedFilterItemsChange([]);
                this.navigateToAnotherTab(newTabIndex);
            });
            return;
        }
        this.onSelectedFilterItemsChange([]);
        this.navigateToAnotherTab(newTabIndex);
    }

    balooMatchChange(match: IBalooMatch, balooMatches: IBalooMatch[]) {
        const unitMatchIndex: number = balooMatches.findIndex((unitMatch: IBalooMatch) => {
            return unitMatch.ObjectId === match.ObjectId;
        });

        balooMatches[unitMatchIndex] = match;
    }

    private navigateToAnotherTab(newTabIndex: number) {
        if (newTabIndex !== BalooSyncTabIndex.User) {
            this.setCurrentTabIndex(newTabIndex);
            return;
        }
        this.isContentShown$.next(false);
        this.showUserImportNotificationDialog();
    }

    isBalooMatchDataChanged(): boolean {
        return this.saveChangesWarningService.hasChanges();
    }

    private showUserImportNotificationDialog() {
        this.fiDialogsService
            .notify({
                isTopCloseButtonShown: true,
                headerIconClass: 'far fa-bookmark',
                headerText: this.appTextService.get('baloo.match_users_dialog_header'),
                messageText: this.appTextService.get('baloo.match_users_dialog_message'),
                noButtonText: this.appTextService.get('common.close'),
                yesButtonText: this.appTextService.get('pk.matching_users_get_users'),
                config: {
                    panelClass: 'fi-rounded-dialog-panel',
                    minWidth: '640px',
                },
            })
            .onYesButtonClick(() => this.importUsers())
            .onNoButtonClick(() => this.setCurrentTabIndex(this.selectedTabIndex$.value));
    }

    getAllGroupMatches() {
        return this.balooMatchData.RoleMatches.concat(this.balooMatchData.UnitMatches);
    }

    private setCurrentTabIndex(tabIndex: number) {
        this.selectedTabIndex$.next(tabIndex);
        this.isContentShown$.next(true);
        this.cdr.markForCheck();
    }

    protected readonly ObjectTypes = ObjectTypes;
    protected readonly BalooSyncTabIndex = BalooSyncTabIndex;
}
